import { Button, Grid, TextField, Typography } from "@material-ui/core";
import React, { useState } from "react";
import SpinnerButton from "../../../../../components/buttons/SpinnerButton";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from "../../../../../components/Dialog";
import Colors from "../../../../../theme/colors";

export type PurchaseAction = "hold" | "release" | "cancel";

interface Props {
  open: boolean;
  action: PurchaseAction;
  category: string;
  amount: string;
  account: string;
  scheduledDate: string;
  handleConfirm: (note: string) => Promise<void>;
  handleExit: () => void;
}

const copy = {
  hold: {
    title: "Hold this payment?",
    body:
      "Every batch will skip this purchase until someone releases or cancels it. Nothing else on the funding request changes.",
    confirm: "Hold payment",
    dismiss: "Back",
    note: true,
    placeholder: "Why is this being held?",
    color: Colors.primary
  },
  release: {
    title: "Release to batch?",
    body:
      "The purchase goes back to Pending For Time Batch and pays in the next batch on or after its scheduled date. If that date has passed, it goes out in the very next batch. The amount is refreshed from the obligation first.",
    confirm: "Release",
    dismiss: "Keep on hold",
    note: false,
    placeholder: "",
    color: Colors.primary
  },
  cancel: {
    title: "Cancel this payment permanently?",
    body:
      "This purchase will never be sent and cannot be released again. The payable it pays stays open, so a new payment can still be issued later with Resubmit.",
    confirm: "Cancel payment",
    dismiss: "Keep on hold",
    note: true,
    placeholder: "Why should this never go out?",
    color: Colors.danger
  }
};

export default ({
  open,
  action,
  category,
  amount,
  account,
  scheduledDate,
  handleConfirm,
  handleExit
}: Props): JSX.Element => {
  const [note, setNote] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const text = copy[action];
  const onConfirm = async () => {
    setIsSubmitting(true);
    try {
      await handleConfirm(note);
    } finally {
      setIsSubmitting(false);
    }
  };
  const details = [
    ["Purchase", `${category} · ${amount}`],
    ["Account", account],
    ["Scheduled", scheduledDate]
  ];
  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogTitle>{text.title}</DialogTitle>
      <DialogContent>
        <Typography style={{ marginBottom: 16 }}>{text.body}</Typography>
        <Grid
          container
          style={{ background: "#F5F5F5", borderRadius: 4, padding: 12 }}
        >
          {details.map(([label, value]) => (
            <React.Fragment key={label}>
              <Grid item xs={3}>
                <Typography color="textSecondary">{label}</Typography>
              </Grid>
              <Grid item xs={9}>
                <Typography>{value}</Typography>
              </Grid>
            </React.Fragment>
          ))}
        </Grid>
        {text.note && (
          <TextField
            label="Note (optional)"
            placeholder={text.placeholder}
            value={note}
            onChange={e => setNote(e.target.value)}
            multiline
            rows={2}
            fullWidth
            margin="normal"
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button color="secondary" onClick={handleExit}>
          {text.dismiss}
        </Button>
        <SpinnerButton
          variant="contained"
          size="small"
          style={{ backgroundColor: text.color, color: Colors.white }}
          onClick={onConfirm}
          disabled={isSubmitting}
        >
          {text.confirm}
        </SpinnerButton>
      </DialogActions>
    </Dialog>
  );
};
